import { useEffect, useState } from "react";
import { Trash2, Save, Plus, GripVertical } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import ImageUploader from "@/components/ImageUploader";
import { useLanguage } from "@/i18n/LanguageContext";

const LANGS = ["tr", "en", "ar"];

const LocalizedField = ({ label, value, onChange, multiline = false, testidPrefix = "" }) => {
  const safe = value || { tr: "", en: "", ar: "" };
  return (
    <div className="space-y-2">
      <Label className="text-[#5C4D3C] uppercase text-xs tracking-[0.2em]">{label}</Label>
      <div className="grid grid-cols-3 gap-3">
        {LANGS.map((lc) => (
          <div key={lc}>
            <div className="text-[10px] uppercase text-[#B5441E] mb-1">{lc}</div>
            {multiline ? (
              <Textarea
                rows={3}
                value={safe[lc] || ""}
                onChange={(e) => onChange({ ...safe, [lc]: e.target.value })}
                data-testid={`${testidPrefix}-${lc}`}
                className="bg-white border-[#E2D9C8]"
                dir={lc === "ar" ? "rtl" : "ltr"}
              />
            ) : (
              <Input
                value={safe[lc] || ""}
                onChange={(e) => onChange({ ...safe, [lc]: e.target.value })}
                data-testid={`${testidPrefix}-${lc}`}
                className="bg-white border-[#E2D9C8]"
                dir={lc === "ar" ? "rtl" : "ltr"}
              />
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

// =================== Site Content tab ===================
export const SiteContentTab = () => {
  const [content, setContent] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const { data } = await api.get("/site/content");
    setContent(
      data || {
        hero_image: "",
        hero_eyebrow: { tr: "", en: "", ar: "" },
        hero_title: { tr: "", en: "", ar: "" },
        hero_subtitle: { tr: "", en: "", ar: "" },
        about_title: { tr: "", en: "", ar: "" },
        about_body_1: { tr: "", en: "", ar: "" },
        about_body_2: { tr: "", en: "", ar: "" },
        stats_years: "150+",
        stats_rooms: "11",
        stats_unesco: "1994",
        stats_breakfast: "100+",
        contact_phone: "",
        contact_email: "",
        contact_address: { tr: "", en: "", ar: "" },
        footer_tagline: { tr: "", en: "", ar: "" },
      },
    );
  };

  useEffect(() => {
    load();
  }, []);

  const update = (k, v) => setContent((prev) => ({ ...prev, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/site/content", content);
      toast.success("OK");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Hata");
    } finally {
      setSaving(false);
    }
  };

  if (!content) return <div className="py-10 text-[#5C4D3C]">Yükleniyor...</div>;

  return (
    <div data-testid="content-tab" className="space-y-8 max-w-4xl">
      <section className="space-y-4 bg-[#FDFBF7] border border-[#E2D9C8] p-6 rounded-sm">
        <h3 className="font-display text-2xl text-[#2D2824]">Hero (Kapak)</h3>
        <ImageUploader
          label="Hero görseli"
          value={content.hero_image}
          onChange={(v) => update("hero_image", v)}
          testid="hero-image"
        />
        <LocalizedField
          label="Üst etiket"
          value={content.hero_eyebrow}
          onChange={(v) => update("hero_eyebrow", v)}
          testidPrefix="hero-eyebrow"
        />
        <LocalizedField
          label="Başlık"
          value={content.hero_title}
          onChange={(v) => update("hero_title", v)}
          testidPrefix="hero-title"
        />
        <LocalizedField
          label="Alt başlık"
          value={content.hero_subtitle}
          onChange={(v) => update("hero_subtitle", v)}
          multiline
          testidPrefix="hero-subtitle"
        />
      </section>

      <section className="space-y-4 bg-[#FDFBF7] border border-[#E2D9C8] p-6 rounded-sm">
        <h3 className="font-display text-2xl text-[#2D2824]">Hakkımızda</h3>
        <LocalizedField
          label="Başlık"
          value={content.about_title}
          onChange={(v) => update("about_title", v)}
          testidPrefix="about-title"
        />
        <LocalizedField
          label="Paragraf 1"
          value={content.about_body_1}
          onChange={(v) => update("about_body_1", v)}
          multiline
          testidPrefix="about-body1"
        />
        <LocalizedField
          label="Paragraf 2"
          value={content.about_body_2}
          onChange={(v) => update("about_body_2", v)}
          multiline
          testidPrefix="about-body2"
        />
        <div className="grid grid-cols-4 gap-3">
          {[
            ["stats_years", "Yıllık tarih"],
            ["stats_rooms", "Oda sayısı"],
            ["stats_unesco", "UNESCO yılı"],
            ["stats_breakfast", "Kahvaltı çeşit"],
          ].map(([k, lbl]) => (
            <div key={k} className="space-y-2">
              <Label className="text-xs uppercase text-[#5C4D3C]">{lbl}</Label>
              <Input
                value={content[k] || ""}
                onChange={(e) => update(k, e.target.value)}
                data-testid={`stats-${k}`}
                className="bg-white border-[#E2D9C8]"
              />
            </div>
          ))}
        </div>
      </section>

      <section className="space-y-4 bg-[#FDFBF7] border border-[#E2D9C8] p-6 rounded-sm">
        <h3 className="font-display text-2xl text-[#2D2824]">İletişim</h3>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-2">
            <Label className="text-xs uppercase text-[#5C4D3C]">Telefon</Label>
            <Input
              value={content.contact_phone || ""}
              onChange={(e) => update("contact_phone", e.target.value)}
              data-testid="contact-phone-input"
              className="bg-white border-[#E2D9C8]"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-xs uppercase text-[#5C4D3C]">E-posta</Label>
            <Input
              value={content.contact_email || ""}
              onChange={(e) => update("contact_email", e.target.value)}
              data-testid="contact-email-input"
              className="bg-white border-[#E2D9C8]"
            />
          </div>
        </div>
        <LocalizedField
          label="Adres"
          value={content.contact_address}
          onChange={(v) => update("contact_address", v)}
          multiline
          testidPrefix="contact-address"
        />
      </section>

      <section className="space-y-4 bg-[#FDFBF7] border border-[#E2D9C8] p-6 rounded-sm">
        <h3 className="font-display text-2xl text-[#2D2824]">WhatsApp</h3>
        <div className="space-y-2">
          <Label className="text-xs uppercase text-[#5C4D3C]">WhatsApp Numarası (ülke koduyla, boşluksuz)</Label>
          <Input
            value={content.whatsapp_number || ""}
            onChange={(e) => update("whatsapp_number", e.target.value)}
            data-testid="whatsapp-number-input"
            placeholder="örn. 905554443322"
            className="bg-white border-[#E2D9C8]"
          />
          <p className="text-[11px] text-[#5C4D3C]">
            Boş bırakırsanız iletişim telefonu kullanılır. Otomatik temizlenir (+ ve boşluklar kaldırılır).
          </p>
        </div>
        <LocalizedField
          label="Önceden doldurulmuş mesaj"
          value={content.whatsapp_message}
          onChange={(v) => update("whatsapp_message", v)}
          multiline
          testidPrefix="whatsapp-message"
        />
      </section>

      <section className="space-y-4 bg-[#FDFBF7] border border-[#E2D9C8] p-6 rounded-sm">
        <h3 className="font-display text-2xl text-[#2D2824]">Footer</h3>
        <LocalizedField
          label="Slogan"
          value={content.footer_tagline}
          onChange={(v) => update("footer_tagline", v)}
          multiline
          testidPrefix="footer-tagline"
        />
      </section>

      <Button
        onClick={save}
        disabled={saving}
        data-testid="content-save"
        className="bg-[#B5441E] hover:bg-[#9D3A19] text-white uppercase tracking-[0.2em] text-xs py-6 px-8"
      >
        <Save size={14} className="me-2" /> {saving ? "Kaydediliyor..." : "Kaydet"}
      </Button>
    </div>
  );
};

// =================== Gallery tab ===================
export const GalleryTab = () => {
  const [items, setItems] = useState([]);
  const [newUrl, setNewUrl] = useState("");
  const [adding, setAdding] = useState(false);

  const load = async () => {
    const { data } = await api.get("/gallery");
    setItems(data);
  };
  useEffect(() => {
    load();
  }, []);

  const add = async () => {
    if (!newUrl) return;
    setAdding(true);
    try {
      await api.post("/admin/gallery", {
        url: newUrl,
        sort_order: items.length,
      });
      setNewUrl("");
      load();
      toast.success("Eklendi");
    } catch {
      toast.error("Hata");
    } finally {
      setAdding(false);
    }
  };

  const del = async (id) => {
    if (!window.confirm("Silmek istediğinize emin misiniz?")) return;
    await api.delete(`/admin/gallery/${id}`);
    load();
  };

  return (
    <div data-testid="gallery-tab" className="space-y-6">
      <div className="bg-[#FDFBF7] border border-[#E2D9C8] p-6 rounded-sm space-y-4">
        <h3 className="font-display text-xl text-[#2D2824]">Yeni Galeri Görseli</h3>
        <ImageUploader
          label="Yeni görsel"
          value={newUrl}
          onChange={setNewUrl}
          testid="gallery-new"
        />
        <Button
          onClick={add}
          disabled={adding || !newUrl}
          data-testid="gallery-add"
          className="bg-[#B5441E] hover:bg-[#9D3A19] text-white"
        >
          <Plus size={14} className="me-2" /> Galeriye Ekle
        </Button>
      </div>
      <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
        {items.map((img) => (
          <div
            key={img.id}
            data-testid={`gallery-item-${img.id}`}
            className="relative aspect-square border border-[#E2D9C8] rounded-sm overflow-hidden group"
          >
            <img src={img.url} alt="" className="w-full h-full object-cover" />
            <button
              onClick={() => del(img.id)}
              data-testid={`gallery-delete-${img.id}`}
              className="absolute top-2 end-2 w-8 h-8 rounded-full bg-black/60 text-white opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
            >
              <Trash2 size={14} />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

// =================== Videos tab ===================
const emptyVideo = () => ({
  title: { tr: "", en: "", ar: "" },
  youtube_id: "",
  thumb: "",
  sort_order: 0,
});

export const VideosTab = () => {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);

  const load = async () => {
    const { data } = await api.get("/videos");
    setItems(data);
  };
  useEffect(() => {
    load();
  }, []);

  const save = async (v) => {
    try {
      if (v.id) {
        await api.patch(`/admin/videos/${v.id}`, v);
      } else {
        await api.post("/admin/videos", v);
      }
      toast.success("OK");
      setEditing(null);
      setCreating(false);
      load();
    } catch {
      toast.error("Hata");
    }
  };

  const del = async (id) => {
    if (!window.confirm("Silmek istediğinize emin misiniz?")) return;
    await api.delete(`/admin/videos/${id}`);
    load();
  };

  const Editor = ({ item, onClose }) => {
    const [v, setV] = useState(item);
    return (
      <div className="space-y-4">
        <LocalizedField
          label="Başlık"
          value={v.title}
          onChange={(val) => setV({ ...v, title: val })}
          testidPrefix="video-title"
        />
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-2">
            <Label className="text-xs uppercase">YouTube ID</Label>
            <Input
              value={v.youtube_id || ""}
              onChange={(e) => setV({ ...v, youtube_id: e.target.value })}
              data-testid="video-youtube"
              placeholder="ör. wfMBx5xAi9Y"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-xs uppercase">Sıra</Label>
            <Input
              type="number"
              value={v.sort_order || 0}
              onChange={(e) => setV({ ...v, sort_order: parseInt(e.target.value || "0", 10) })}
              data-testid="video-sort"
            />
          </div>
        </div>
        <ImageUploader
          label="Kapak görseli"
          value={v.thumb}
          onChange={(u) => setV({ ...v, thumb: u })}
          testid="video-thumb"
        />
        <Button
          onClick={() => save(v)}
          data-testid="video-save"
          className="bg-[#B5441E] hover:bg-[#9D3A19] text-white"
        >
          <Save size={14} className="me-2" /> Kaydet
        </Button>
      </div>
    );
  };

  return (
    <div data-testid="videos-tab" className="space-y-4">
      <div className="flex justify-end">
        <Dialog open={creating} onOpenChange={setCreating}>
          <DialogTrigger asChild>
            <Button data-testid="video-add" className="bg-[#B5441E] hover:bg-[#9D3A19] text-white">
              <Plus size={14} className="me-2" /> Yeni Video
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader><DialogTitle>Yeni Video</DialogTitle></DialogHeader>
            <Editor item={emptyVideo()} />
          </DialogContent>
        </Dialog>
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        {items.map((v) => (
          <div
            key={v.id}
            data-testid={`video-item-${v.id}`}
            className="bg-[#FDFBF7] border border-[#E2D9C8] rounded-sm overflow-hidden"
          >
            <img src={v.thumb} alt="" className="w-full aspect-video object-cover" />
            <div className="p-4 flex items-center justify-between">
              <div>
                <div className="font-display text-lg">{v.title?.tr}</div>
                <div className="text-xs text-[#5C4D3C] ltr-numbers">{v.youtube_id}</div>
              </div>
              <div className="flex gap-2">
                <Dialog open={editing?.id === v.id} onOpenChange={(o) => !o && setEditing(null)}>
                  <DialogTrigger asChild>
                    <button
                      data-testid={`video-edit-${v.id}`}
                      onClick={() => setEditing(v)}
                      className="text-[#B5441E]"
                    >
                      ✎
                    </button>
                  </DialogTrigger>
                  {editing?.id === v.id && (
                    <DialogContent className="max-w-2xl">
                      <DialogHeader><DialogTitle>Düzenle</DialogTitle></DialogHeader>
                      <Editor item={editing} />
                    </DialogContent>
                  )}
                </Dialog>
                <button
                  data-testid={`video-delete-${v.id}`}
                  onClick={() => del(v.id)}
                  className="text-[#B5441E]"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// =================== Attractions tab ===================
const emptyAttraction = () => ({
  title: { tr: "", en: "", ar: "" },
  description: { tr: "", en: "", ar: "" },
  image: "",
  sort_order: 0,
});

export const AttractionsTab = () => {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);

  const load = async () => {
    const { data } = await api.get("/attractions");
    setItems(data);
  };
  useEffect(() => {
    load();
  }, []);

  const save = async (a) => {
    try {
      if (a.id) {
        await api.patch(`/admin/attractions/${a.id}`, a);
      } else {
        await api.post("/admin/attractions", a);
      }
      toast.success("OK");
      setEditing(null);
      setCreating(false);
      load();
    } catch {
      toast.error("Hata");
    }
  };

  const del = async (id) => {
    if (!window.confirm("Silmek istediğinize emin misiniz?")) return;
    await api.delete(`/admin/attractions/${id}`);
    load();
  };

  const Editor = ({ item }) => {
    const [a, setA] = useState(item);
    return (
      <div className="space-y-4">
        <LocalizedField
          label="Başlık"
          value={a.title}
          onChange={(v) => setA({ ...a, title: v })}
          testidPrefix="attr-title"
        />
        <LocalizedField
          label="Açıklama"
          value={a.description}
          onChange={(v) => setA({ ...a, description: v })}
          multiline
          testidPrefix="attr-desc"
        />
        <div className="space-y-2">
          <Label className="text-xs uppercase">Sıra</Label>
          <Input
            type="number"
            value={a.sort_order || 0}
            onChange={(e) => setA({ ...a, sort_order: parseInt(e.target.value || "0", 10) })}
            data-testid="attr-sort"
          />
        </div>
        <ImageUploader
          label="Görsel"
          value={a.image}
          onChange={(u) => setA({ ...a, image: u })}
          testid="attr-image"
        />
        <Button
          onClick={() => save(a)}
          data-testid="attr-save"
          className="bg-[#B5441E] hover:bg-[#9D3A19] text-white"
        >
          <Save size={14} className="me-2" /> Kaydet
        </Button>
      </div>
    );
  };

  return (
    <div data-testid="attractions-tab" className="space-y-4">
      <div className="flex justify-end">
        <Dialog open={creating} onOpenChange={setCreating}>
          <DialogTrigger asChild>
            <Button data-testid="attr-add" className="bg-[#B5441E] hover:bg-[#9D3A19] text-white">
              <Plus size={14} className="me-2" /> Yeni Cazibe Merkezi
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader><DialogTitle>Yeni</DialogTitle></DialogHeader>
            <Editor item={emptyAttraction()} />
          </DialogContent>
        </Dialog>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((a) => (
          <div
            key={a.id}
            data-testid={`attr-item-${a.id}`}
            className="bg-[#FDFBF7] border border-[#E2D9C8] rounded-sm overflow-hidden"
          >
            <img src={a.image} alt="" className="w-full aspect-[4/3] object-cover" />
            <div className="p-4 flex items-center justify-between gap-2">
              <div>
                <div className="font-display text-lg">{a.title?.tr}</div>
                <div className="text-xs text-[#5C4D3C] line-clamp-2">{a.description?.tr}</div>
              </div>
              <div className="flex gap-2 shrink-0">
                <Dialog open={editing?.id === a.id} onOpenChange={(o) => !o && setEditing(null)}>
                  <DialogTrigger asChild>
                    <button
                      data-testid={`attr-edit-${a.id}`}
                      onClick={() => setEditing(a)}
                      className="text-[#B5441E]"
                    >
                      ✎
                    </button>
                  </DialogTrigger>
                  {editing?.id === a.id && (
                    <DialogContent className="max-w-2xl">
                      <DialogHeader><DialogTitle>Düzenle</DialogTitle></DialogHeader>
                      <Editor item={editing} />
                    </DialogContent>
                  )}
                </Dialog>
                <button
                  data-testid={`attr-delete-${a.id}`}
                  onClick={() => del(a.id)}
                  className="text-[#B5441E]"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// =================== Testimonials tab ===================
const emptyTestimonial = () => ({
  name: { tr: "", en: "", ar: "" },
  location: { tr: "", en: "", ar: "" },
  text: { tr: "", en: "", ar: "" },
  rating: 5,
  sort_order: 0,
});

export const TestimonialsTab = () => {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);

  const load = async () => {
    const { data } = await api.get("/testimonials");
    setItems(data);
  };
  useEffect(() => {
    load();
  }, []);

  const save = async (t_) => {
    try {
      if (t_.id) {
        await api.patch(`/admin/testimonials/${t_.id}`, t_);
      } else {
        await api.post("/admin/testimonials", t_);
      }
      toast.success("OK");
      setEditing(null);
      setCreating(false);
      load();
    } catch {
      toast.error("Hata");
    }
  };

  const del = async (id) => {
    if (!window.confirm("Silmek istediğinize emin misiniz?")) return;
    await api.delete(`/admin/testimonials/${id}`);
    load();
  };

  const Editor = ({ item }) => {
    const [t_, setT] = useState(item);
    return (
      <div className="space-y-4">
        <LocalizedField
          label="İsim"
          value={t_.name}
          onChange={(v) => setT({ ...t_, name: v })}
          testidPrefix="tm-name"
        />
        <LocalizedField
          label="Konum"
          value={t_.location}
          onChange={(v) => setT({ ...t_, location: v })}
          testidPrefix="tm-location"
        />
        <LocalizedField
          label="Yorum"
          value={t_.text}
          onChange={(v) => setT({ ...t_, text: v })}
          multiline
          testidPrefix="tm-text"
        />
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-2">
            <Label className="text-xs uppercase">Puan (1-5)</Label>
            <Input
              type="number"
              min={1}
              max={5}
              value={t_.rating || 5}
              onChange={(e) => setT({ ...t_, rating: parseInt(e.target.value || "5", 10) })}
              data-testid="tm-rating"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-xs uppercase">Sıra</Label>
            <Input
              type="number"
              value={t_.sort_order || 0}
              onChange={(e) => setT({ ...t_, sort_order: parseInt(e.target.value || "0", 10) })}
              data-testid="tm-sort"
            />
          </div>
        </div>
        <Button
          onClick={() => save(t_)}
          data-testid="tm-save"
          className="bg-[#B5441E] hover:bg-[#9D3A19] text-white"
        >
          <Save size={14} className="me-2" /> Kaydet
        </Button>
      </div>
    );
  };

  return (
    <div data-testid="testimonials-cms-tab" className="space-y-4">
      <div className="flex justify-end">
        <Dialog open={creating} onOpenChange={setCreating}>
          <DialogTrigger asChild>
            <Button data-testid="tm-add" className="bg-[#B5441E] hover:bg-[#9D3A19] text-white">
              <Plus size={14} className="me-2" /> Yeni Yorum
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader><DialogTitle>Yeni Yorum</DialogTitle></DialogHeader>
            <Editor item={emptyTestimonial()} />
          </DialogContent>
        </Dialog>
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        {items.map((t_) => (
          <div
            key={t_.id}
            data-testid={`tm-item-${t_.id}`}
            className="bg-[#FDFBF7] border border-[#E2D9C8] p-5 rounded-sm"
          >
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="font-display text-lg">{t_.name?.tr}</div>
                <div className="text-xs text-[#5C4D3C]">{t_.location?.tr} · ★ {t_.rating}</div>
              </div>
              <div className="flex gap-2">
                <Dialog open={editing?.id === t_.id} onOpenChange={(o) => !o && setEditing(null)}>
                  <DialogTrigger asChild>
                    <button
                      data-testid={`tm-edit-${t_.id}`}
                      onClick={() => setEditing(t_)}
                      className="text-[#B5441E]"
                    >
                      ✎
                    </button>
                  </DialogTrigger>
                  {editing?.id === t_.id && (
                    <DialogContent className="max-w-2xl">
                      <DialogHeader><DialogTitle>Düzenle</DialogTitle></DialogHeader>
                      <Editor item={editing} />
                    </DialogContent>
                  )}
                </Dialog>
                <button
                  data-testid={`tm-delete-${t_.id}`}
                  onClick={() => del(t_.id)}
                  className="text-[#B5441E]"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
            <p className="text-sm text-[#2D2824] mt-3 line-clamp-3">{t_.text?.tr}</p>
          </div>
        ))}
      </div>
    </div>
  );
};
